import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import Logo from '../components/Logo';
import { createPlatformApi } from '../utils/platformApi';

const fmt = (n) => new Intl.NumberFormat().format(n);

export default function BillingPlansPage() {
    const { t } = useTranslation(['platformBilling', 'common']);
    const [plans, setPlans] = useState([]);
    const [annual, setAnnual] = useState(false);
    const [currentPlanId, setCurrentPlanId] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('token');

    useEffect(() => {
        load();
    }, []);

    async function load() {
        setLoading(true);
        try {
            const res = await axios.get(`${getApiBaseUrl()}/platform/plans`);
            setPlans(res.data || []);
            if (token) {
                const api = createPlatformApi();
                const planRes = await api.get('/platform/billing/current-plan');
                setCurrentPlanId(planRes.data?.plan?.id);
            }
        } catch {
            setPlans([]);
        }
        setLoading(false);
    }

    const inf = t('platformBilling:plansPage.infinity');

    return (
        <OwnerPageLayout
            title={t('platformBilling:plansPage.title')}
            subtitle={t('platformBilling:plansPage.subtitle')}
            maxWidth={1200}
            actions={<Logo variant="light" height={40} />}
        >

                <div style={{
                    display: 'inline-flex', background: '#fff', borderRadius: '12px',
                    padding: '4px', marginBottom: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                    <button type="button" onClick={() => setAnnual(false)}
                        style={toggleBtn(!annual)}>{t('platformBilling:plansPage.monthly')}</button>
                    <button type="button" onClick={() => setAnnual(true)}
                        style={toggleBtn(annual)}>{t('platformBilling:plansPage.annual')}</button>
                </div>

                {loading && <p>{t('platformBilling:plansPage.loadingPlans')}</p>}

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                }}>
                    {plans.map((p) => {
                        const isRecommended = p.name === 'academy';
                        const isCurrent = currentPlanId === p.id;
                        const monthlyDisplay = annual && p.annualMonthlyEquivalent
                            ? p.annualMonthlyEquivalent
                            : p.monthlyPrice;
                        const studentLimit = p.baseStudentLimit === 999999 ? inf : p.baseStudentLimit;
                        const schoolsLimit = p.maxSchools === 999999 ? inf : p.maxSchools;
                        const extraStudent = p.extraStudentPricePerMonth > 0
                            ? t('platformBilling:plansPage.extraStudent', { price: `${fmt(p.extraStudentPricePerMonth)} ₽/мес` })
                            : t('platformBilling:plansPage.extraStudentNone');

                        return (
                            <div key={p.id} style={{
                                background: '#fff',
                                borderRadius: '16px',
                                padding: '20px',
                                border: isRecommended ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                                boxShadow: isRecommended ? '0 8px 24px rgba(79,70,229,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                                position: 'relative'
                            }}>
                                {isRecommended && (
                                    <span style={{
                                        position: 'absolute', top: '-10px', right: '12px',
                                        background: '#4f46e5', color: '#fff', fontSize: '11px',
                                        padding: '4px 10px', borderRadius: '20px', fontWeight: 700
                                    }}>{t('platformBilling:plansPage.recommended')}</span>
                                )}
                                <h3 style={{ margin: '0 0 4px', fontSize: '20px' }}>{p.displayName}</h3>
                                <p style={{ margin: '0 0 16px', color: '#888', fontSize: '13px' }}>{p.name}</p>
                                <div style={{ fontSize: '32px', fontWeight: 800, color: '#1A1A1A' }}>
                                    {fmt(monthlyDisplay)} ₽
                                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#888' }}>{t('platformBilling:plansPage.perMonth')}</span>
                                </div>
                                {annual && p.annualDiscountPercent > 0 && (
                                    <div style={{
                                        marginTop: '8px', display: 'inline-block',
                                        background: '#ecfdf5', color: '#059669',
                                        padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600
                                    }}>
                                        {t('platformBilling:plansPage.discount', { percent: p.annualDiscountPercent })}
                                    </div>
                                )}
                                <ul style={{ margin: '16px 0', paddingLeft: '18px', color: '#444', fontSize: '14px', lineHeight: 1.7 }}>
                                    <li>{t('platformBilling:plansPage.studentsLimit', { count: studentLimit })}</li>
                                    <li>{t('platformBilling:plansPage.schoolsLimit', { count: schoolsLimit })}</li>
                                    <li>{extraStudent}</li>
                                </ul>
                                {token ? (
                                    isCurrent ? (
                                        <button type="button" disabled style={btnDisabled}>{t('platformBilling:plansPage.currentPlan')}</button>
                                    ) : (
                                        <Link to="/billing" state={{ selectPlanId: p.id, billingPeriod: annual ? 'annual' : 'monthly' }}
                                            style={btnPrimary}>{t('platformBilling:plansPage.selectPlan')}</Link>
                                    )
                                ) : (
                                    <Link to="/login" style={btnPrimary}>{t('platformBilling:plansPage.loginToSelect')}</Link>
                                )}
                            </div>
                        );
                    })}
                </div>
        </OwnerPageLayout>
    );
}

const toggleBtn = (active) => ({
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    background: active ? '#4f46e5' : 'transparent',
    color: active ? '#fff' : '#666'
});

const btnPrimary = {
    display: 'block', textAlign: 'center', padding: '12px',
    background: '#4f46e5', color: '#fff', borderRadius: '10px',
    textDecoration: 'none', fontWeight: 600, fontSize: '14px'
};

const btnDisabled = {
    width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
    background: '#e5e7eb', color: '#888', fontWeight: 600, cursor: 'default'
};
